import { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, CalendarDays, Film } from "lucide-react";
import { mediaUrl, fmtDuration } from "@/lib/academy";

export const LessonPlayerDialog = ({ lesson, onClose, onComplete, onUncomplete }) => {
  const fired = useRef(false);
  if (!lesson) return null;
  const onTime = (e) => {
    const v = e.currentTarget;
    if (!fired.current && !lesson.completed && v.duration && v.currentTime / v.duration >= 0.9) { fired.current = true; onComplete(lesson); }
  };
  const updated = lesson.updated_at ? new Date(lesson.updated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
  return (
    <Dialog open={!!lesson} onOpenChange={(o) => { if (!o) { fired.current = false; onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="academy-player-dialog">
        <DialogHeader>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary">{lesson.category}</div>
          <DialogTitle className="font-heading text-xl" data-testid="academy-player-title">{lesson.title}</DialogTitle>
          <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <span className="flex items-center gap-1" data-testid="academy-player-duration"><Clock className="w-3.5 h-3.5" />{fmtDuration(lesson.duration)}</span>
            <span className="flex items-center gap-1" data-testid="academy-player-updated"><CalendarDays className="w-3.5 h-3.5" />Updated {updated}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl overflow-hidden bg-black aspect-video">
          {lesson.video_url ? (
            <video key={lesson.id} src={mediaUrl(lesson.video_url)} poster={mediaUrl(lesson.thumbnail_url)} controls playsInline className="w-full h-full" onTimeUpdate={onTime} onEnded={() => !lesson.completed && onComplete(lesson)} data-testid="academy-video-player" />
          ) : (
            <div className="relative w-full h-full" data-testid="academy-video-coming-soon">
              {lesson.thumbnail_url && <img src={mediaUrl(lesson.thumbnail_url)} alt="" className="w-full h-full object-cover opacity-40" />}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-2"><Film className="w-10 h-10" /><div className="font-heading text-lg font-semibold">Video coming soon</div><div className="text-sm text-white/70">This lesson is being recorded. Check back shortly.</div></div>
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed" data-testid="academy-player-description">{lesson.description}</p>
        <div className="flex flex-wrap items-center gap-3">
          {lesson.completed ? (
            <>
              <span className="text-sm text-emerald-500 font-medium flex items-center gap-1.5" data-testid="academy-player-completed"><CheckCircle2 className="w-4 h-4" />Completed</span>
              <Button size="sm" variant="ghost" onClick={() => onUncomplete(lesson)} data-testid="academy-mark-incomplete-btn">Mark as not watched</Button>
            </>
          ) : (
            <Button size="sm" onClick={() => onComplete(lesson)} data-testid="academy-mark-complete-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" />Mark as complete</Button>
          )}
        </div>
        <div className="rounded-xl border border-border p-4 space-y-2" data-testid="academy-player-transcript">
          <div className="text-sm font-semibold">Transcript</div>
          <div className="text-sm text-muted-foreground whitespace-pre-line max-h-56 overflow-y-auto">{lesson.transcript?.trim() || "A transcript will be available when this video is published."}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
